import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { useSocketAuthenticated } from "../../context/Socket/SocketContext";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
  textField: { flex: 1, },

  floatingButton: {
    transition: "transform 0.3s",
    "&:hover": { transform: "translateY(-5px)", },
  },
}));

const NewInternalChatModal = ({ modalOpen, onClose, internalChatSelectedUserId = null, setTabOpen = null }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { onlineUsers, updateOnlineUsers } = useSocketAuthenticated();
  const { user: loggedInUser } = useContext(AuthContext);

  const [chatName, setChatName] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");

  const { allUsers, setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (modalOpen) {
      const updateOnlineUsersList = async () => await updateOnlineUsers();
      updateOnlineUsersList();
    }

    if (modalOpen && ![null, undefined].includes(internalChatSelectedUserId)) {
      setSelectedUserId(internalChatSelectedUserId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalChatSelectedUserId, modalOpen]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose =() => {
    onClose();
    setSelectedUserId("");
    setChatName("");
  };

  const handleChatNameChange = (event) => { setChatName(event.target.value); };

  const handleSelectedUserChange = (event) => { setSelectedUserId(event.target.value); };

  const handleSaveInternalChat = async (event) => {
    event.preventDefault();
    setLoading(true);

    if ([null, undefined].includes(chatName) || chatName.length < 3) {
      toast.info(i18n.t("newInternalChatModal.toasts.chatNameInfo"));
    }
    
    else if ([null, undefined].includes(selectedUserId) || selectedUserId === "") {
      toast.info(i18n.t("newInternalChatModal.toasts.selectedUserIdInfo"));
    }

    else {
      try {
        const { data: ticket } = await api.post("/internalChats", {
          name: chatName.trim(),
          userId: loggedInUser.id,
          userIdInternalChat: selectedUserId,
          noteCreatedBy: loggedInUser.id
        });

        DefaultFunctionsUtils.redirectToTicketsPage(history, `/${ticket.id}`);
        setShouldSyncRouteOnMenu(true);

        if (setTabOpen !== null && setTabOpen !== undefined) setTabOpen("open");
        localStorage.setItem("tabOpenTickets", "open");

        handleClose();
      } catch (error) {
        console.log("Handle Save Internal Chat Error:", error);
        toastError(error);
      }  
    }

    setLoading(false);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" scroll="paper">
      <form onSubmit={handleSaveInternalChat}>
        {/* 
          ***********
          ** Title **
          ***********
        */}
        <DialogTitle>
          {i18n.t("newInternalChatModal.title")}
        </DialogTitle>



        {/* 
          *************
          ** Content **
          *************
        */}
        <DialogContent dividers>
          {/* 
            ****************
            ** Name Field **
            ****************
          */}
          <TextField
            value={chatName}
            label={i18n.t("newInternalChatModal.form.name")}
            autoFocus
            name="name"
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField}
            multiline={false}
            onChange={handleChatNameChange}
          />



          <br />
          <Divider />
          <br />



          {/* 
            ***********************
            ** User Select Field **
            ***********************
          */}
          <FormControl variant="outlined" fullWidth>
            <InputLabel>{i18n.t("newInternalChatModal.form.user")}</InputLabel>

            <Select
              value={selectedUserId}
              onChange={handleSelectedUserChange}
              label={i18n.t("newInternalChatModal.form.user")}
            >
              <MenuItem value={""}>&nbsp;</MenuItem>

              {/* ***---- Online Users ----*** */}
              <ListSubheader>{i18n.t("newInternalChatModal.listSubheaders.onlineUsers")}</ListSubheader>
              {allUsers.map(user => {
                return (onlineUsers || []).filter(onlineUser => onlineUser.userId !== loggedInUser.id).some(onlineUser => onlineUser.userId === user.id)
                  ? <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                  : <></>;
              })}

              {/* ***---- Offline Users ----*** */}
              <ListSubheader>{i18n.t("newInternalChatModal.listSubheaders.offlineUsers")}</ListSubheader>
              {allUsers.map(user => {
                return !(onlineUsers || []).some(onlineUser => onlineUser.userId === user.id)
                  ? <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                  : <></>;
              })}
            </Select>
          </FormControl>
        </DialogContent>



        {/* 
          ********************
          ** Dialog Actions **
          ********************
        */}
        <DialogActions>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={loading}
            variant="outlined"
            className={classes.floatingButton}
          >
            {i18n.t("newInternalChatModal.buttons.cancel")}
          </Button>

          <ButtonWithSpinner
            variant="contained"
            type="submit"
            color="primary"
            loading={loading}
            className={classes.floatingButton}
          >
            {i18n.t("newInternalChatModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewInternalChatModal;